import { useDayPeriodStore } from '../../store/useDayPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { DayPeriodPicker } from './shared/DayPeriodPicker'
import { MinGrid } from './shared/MinGrid'
import { TimeZonePicker } from './shared/TimeZonePicker'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useMinOneDayData } from '../../hooks/useMinOneDayData'
import { cldWindVal2Time10M } from './shared/weatherUtils'

const SLUG = 'min-prehled'

export const MIN_COLUMNS = [
  {
    group: 'Teploty',
    subgroups: null,
    cols: [
      { tsId: '10M_TEMP',    label: 'Teplota', unit: '°C' },
      { tsId: '10M_TN',      label: 'Min.',    unit: '°C' },
      { tsId: '10M_TX',      label: 'Max.',    unit: '°C' },
      { tsId: '10M_TGROUND', label: 'Přízemní',   unit: '°C' },
    ],
  },
  {
    group: 'Vítr',
    subgroups: [
      {
        label: 'Okamžitý',
        cols: [
          { tsId: '10M_WS', label: 'Rychlost', unit: 'm/s' },
          { tsId: '10M_WD', label: 'Směr',     unit: '°'   },
        ],
      },
      {
        label: 'Průměrný',
        cols: [
          { tsId: '10M_WSAVG', label: 'Rychlost', unit: 'm/s' },
          { tsId: '10M_WDAVG', label: 'Směr',     unit: '°'   },
        ],
      },
      {
        label: 'Maximální',
        cols: [
          { tsId: '10M_WSMAX', label: 'Rychlost', unit: 'm/s'   },
          { tsId: '10M_WDMAX', label: 'Směr',     unit: '°'     },
          { tsId: '10M_WTMAX', label: 'Čas max.', unit: 'HH:mm', formatCell: (v, time) => cldWindVal2Time10M(v, time) },
        ],
      },
    ],
    cols: null,
  },
  {
    group: 'Tlak',
    subgroups: null,
    cols: [
      { tsId: '10M_PRESS', label: 'Tlak', unit: 'hPa' },
    ],
  },
  {
    group: 'Vlhkost',
    subgroups: null,
    cols: [
      { tsId: '10M_RAWHUMIDITY', label: 'Vlhkost', unit: '%' },
    ],
  },
  {
    group: 'Půdní teploty',
    subgroups: null,
    cols: [
      { tsId: '10M_SOIL05',  label: '5 cm',   unit: '°C' },
      { tsId: '10M_SOIL10',  label: '10 cm',  unit: '°C' },
      { tsId: '10M_SOIL20',  label: '20 cm',  unit: '°C' },
      { tsId: '10M_SOIL50',  label: '50 cm',  unit: '°C' },
      { tsId: '10M_SOIL100', label: '100 cm', unit: '°C' },
    ],
  },
]


// Transponovaná hlavička (řádky = veličiny). Skupina (row.group) → oddělovací řádek hlavičky.
export const MIN_HEADER_TRANSPOSED = {
  groupLabel: 'Veličina',
  rows: [
    { tsId: '10M_TEMP',    group: 'Teploty',       label: 'Teplota',                unit: '°C' },
    { tsId: '10M_TN',      group: 'Teploty',       label: 'Minimální teplota',      unit: '°C' },
    { tsId: '10M_TX',      group: 'Teploty',       label: 'Maximální teplota',      unit: '°C' },
    { tsId: '10M_TGROUND', group: 'Teploty',       label: 'Přízemní teplota',       unit: '°C' },
    { tsId: '10M_WS',      group: 'Vítr',          label: 'Okamžitá rychlost větru', unit: 'm/s' },
    { tsId: '10M_WD',      group: 'Vítr',          label: 'Okamžitý směr větru',    unit: '°' },
    { tsId: '10M_WSAVG',   group: 'Vítr',          label: 'Průměrná rychlost větru', unit: 'm/s' },
    { tsId: '10M_WDAVG',   group: 'Vítr',          label: 'Průměrný směr větru',    unit: '°' },
    { tsId: '10M_WSMAX',   group: 'Vítr',          label: 'Maximální rychlost větru', unit: 'm/s' },
    { tsId: '10M_WDMAX',   group: 'Vítr',          label: 'Směr maximálního větru', unit: '°' },
    { tsId: '10M_WTMAX',   group: 'Vítr',          label: 'Čas maxima větru',       unit: 'HH:mm', formatCell: (v, time) => cldWindVal2Time10M(v, time) },
    { tsId: '10M_PRESS',   group: 'Tlak',          label: 'Tlak',                   unit: 'hPa' },
    { tsId: '10M_RAWHUMIDITY', group: 'Vlhkost',   label: 'Relativní vlhkost',      unit: '%' },
    { tsId: '10M_SOIL05',  group: 'Půdní teploty', label: 'Půdní teplota 5 cm',     unit: '°C' },
    { tsId: '10M_SOIL10',  group: 'Půdní teploty', label: 'Půdní teplota 10 cm',    unit: '°C' },
    { tsId: '10M_SOIL20',  group: 'Půdní teploty', label: 'Půdní teplota 20 cm',    unit: '°C' },
    { tsId: '10M_SOIL50',  group: 'Půdní teploty', label: 'Půdní teplota 50 cm',    unit: '°C' },
    { tsId: '10M_SOIL100', group: 'Půdní teploty', label: 'Půdní teplota 100 cm',   unit: '°C' },
  ],
}


export function MinOneDayView({ station }) {
  const { date, setDate } = useDayPeriodStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap, labelMap, dayMap, isLoading, isError } = useMinOneDayData(station?.gh_id, date, MIN_COLUMNS)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <DayPeriodPicker
          date={date}
          onChange={setDate}
          obsType="10M"
          headerExtra={<TimeZonePicker trailing={<TableOrientationToggle slug={SLUG} />} />}
        />
      </div>

      {/* Grid area */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Načítání dat…
          </div>
        )}
        {isError && (
          <p className="text-red-500 text-sm">Chyba při načítání dat</p>
        )}
        {!isLoading && !isError && (
          <MinGrid
            columns={MIN_COLUMNS}
            headerTransposed={MIN_HEADER_TRANSPOSED}
            orientation={orientation}
            dataMap={dataMap}
            labelMap={labelMap}
            dayMap={dayMap}
            rowGroupBorder={(_, label) => label.endsWith(':00')}
          />
        )}
      </div>
    </div>
  )
}
